"use client";

import React from "react";
import FlashBanner from "@/components/FlashBanner";
import Site from "@/components/Site";
import { Tracking } from "@/components/Tracking";
import AOS from "aos";
import { PageProps } from "@/types";
import {
  headerToHeroMap,
  sectionsNavSettingsFallback,
} from "@/components/section-fallbacks";
import { getGlobalStyle } from "@/helpers/globalStyles";
import "aos/dist/aos.css";
import dynamic from "next/dynamic";
import {SectionSkeleton} from "@/components/SectionSkeleton";

const Section = dynamic(() => import("../../../../components/Section") , {
  ssr: false,
  loading: () => <SectionSkeleton/>,
});

export default function WebsitePage({
  pageData: {
    websiteId,
    page,
    plan,
    colors,
    universalData,
    universalSections,
    defaultLocale,
    pages,
    trackingCodes,
    animation,
    isUnderMaintenance,
    maintenancePages,
    theme,
  },
}: {
  pageData: PageProps;
}) {

  React.useEffect(() => {
    AOS.init();
  }, []);

  const currentLocale = page?.language;

  const isOldHomeHeader =
    !universalSections?.header_expanded[currentLocale]?.section_settings ||
    !!universalSections?.header_expanded[currentLocale]?.section_settings
      ?.background;

  const currentGlobalHeader = universalSections.header_expanded[currentLocale];

  const universalSectionsWithFallback = {
    ...universalSections,
    header_expanded: {
      ...universalSections.header_expanded,
      [currentLocale]: {
        ...currentGlobalHeader,
        section_settings: isOldHomeHeader
          ? {
              ...sectionsNavSettingsFallback[currentGlobalHeader.section_design]
                .section_settings,
              ...(!page.home_page &&
              sectionsNavSettingsFallback[currentGlobalHeader.section_design]
                .section_settings.style.isTransparent &&
              sectionsNavSettingsFallback[currentGlobalHeader.section_design]
                .section_settings.colors.navLinksColor === "#000000"
                ? {
                    style: {
                      ...sectionsNavSettingsFallback[
                        currentGlobalHeader.section_design
                      ].section_settings.styles,
                      isTransparent: false,
                    },
                  }
                : {}),
            }
          : currentGlobalHeader.section_settings,
      },
    },
  };

  const globalHeaderWithFallback =
    universalSectionsWithFallback?.header_expanded[currentLocale];

  const isGlobalNavbarTransparent =
    !!globalHeaderWithFallback?.section_settings?.style?.isTransparent;

  const pageSectionsWithFallback =
    isOldHomeHeader && page.home_page
      ? [
          {
            ...globalHeaderWithFallback,
            section_settings: { background: {} } as any,
            section_design:
              headerToHeroMap[globalHeaderWithFallback.section_design],
          },
          ...page.page_sections,
        ]
      : page.page_sections;

  const maintenancePage = maintenancePages.find(
    (page) => page.language === currentLocale
  );

  return (
    <>
      <head>
        <style
          dangerouslySetInnerHTML={{
            __html: theme
              ? `
                .siteBody {
                  --theme-color-brand1: ${theme.colors.brand1};
                  --theme-color-brand2: ${theme.colors.brand2};
                  --theme-color-brand3: ${theme.colors.brand3};
                  --theme-color-brand4: ${theme.colors.brand4};
                  --theme-color-secondary1: ${theme.colors.secondary1};
                  --theme-color-secondary2: ${theme.colors.secondary2};
                  --theme-color-secondary3: ${theme.colors.secondary3};
                  --theme-color-secondary4: ${theme.colors.secondary4};
                  --theme-color-base1: ${theme.colors.base1};
                  --theme-color-base2: ${theme.colors.base2};
                  --theme-color-base3: ${theme.colors.base3};
                  --theme-color-base4: ${theme.colors.base4};
                  --theme-color-base5: ${theme.colors.base5};
                  --theme-color-accent1: ${theme.colors.accent1};
                  --theme-color-accent2: ${theme.colors.accent2};
                  --white-background-color: white;
                  --theme-transparent-color: transparent;
                }
              `
              : `
                .siteBody {
                  --theme-color-primary: ${colors[0]};
                  --wt-btn-primary-bg-color: ${colors[0]};
                  --theme-color-secondary: ${colors[1]};
                  --theme-color-gradient: ${colors[3]};
                }
              `,
          }}
        />
        {theme && (
          <style
            dangerouslySetInnerHTML={{
              __html: getGlobalStyle(theme),
            }}
          />
        )}
      </head>
      <Tracking codes={trackingCodes} />
      {Number(plan?.id) === 1 ? <FlashBanner /> : null}
      <Site
        websiteId={websiteId}
        page={page}
        pages={pages}
        universalSections={universalSectionsWithFallback}
        universalData={universalData}
        currentLocale={page?.language}
        defaultLocale={defaultLocale}
        animation={animation}
        isUnderMaintenance={isUnderMaintenance}
        theme={theme}
      >
        {isUnderMaintenance ? (
          <div className="maintenance-section">
            {maintenancePage?.page_sections.map((section) => (
              <Section
                key={section.id}
                id={section.id}
                universal={{ ...universalData, pageName: page.name }}
                design={section.section_design}
                values={section.section_value}
                animation={theme ? theme.animation : animation}
                sectionSettings={section.section_settings}
                isFirstAndNavTransparent={false}
              />
            ))}
          </div>
        ) : (
          pageSectionsWithFallback.map((section, index) => (
            <Section
              key={section.id}
              id={section.id}
              universal={{ ...universalData, pageName: page.name }}
              design={section.section_design}
              values={section.section_value}
              animation={theme ? theme.animation : animation}
              sectionSettings={section.section_settings}
              isFirstAndNavTransparent={
                index === 0 && isGlobalNavbarTransparent
              }
            />
          ))
        )}
      </Site>
    </>
  );
}
